import { MAPBOX_TOKEN } from '../config'

/**
 * Normalize polygon coordinates to ensure valid GeoJSON structure.
 * Handles cases where coordinates have extra nesting levels (e.g., [[[coords]]] instead of [[coords]]).
 */
export const normalizePolygonCoordinates = (coordinates: any): number[][][] => {
  // Handle invalid structure with extra nesting
  // Check if we have an extra array level (e.g., [[[[lon, lat]]]] instead of [[[lon, lat]]])
  if (
    Array.isArray(coordinates) &&
    coordinates.length === 1 &&
    Array.isArray(coordinates[0]) &&
    coordinates[0].length > 0 &&
    Array.isArray(coordinates[0][0]) &&
    coordinates[0][0].length > 0 &&
    Array.isArray(coordinates[0][0][0]) &&
    typeof coordinates[0][0][0][0] === 'number'
  ) {
    // Remove the extra nesting level
    return coordinates[0]
  }

  // If structure looks correct (array of rings, each ring is array of [lon, lat] pairs)
  if (
    Array.isArray(coordinates) &&
    coordinates.length > 0 &&
    Array.isArray(coordinates[0]) &&
    coordinates[0].length > 0 &&
    Array.isArray(coordinates[0][0]) &&
    typeof coordinates[0][0][0] === 'number'
  ) {
    return coordinates
  }

  // Fallback: return empty array for invalid coordinates
  console.warn('Invalid polygon coordinates structure:', coordinates)
  return []
}

/**
 * Simplify polygon to reduce number of points while maintaining shape.
 * Useful for fitting polygons within URL length limits.
 *
 * @param coordinates - Polygon coordinates in GeoJSON format
 * @param maxPoints - Maximum number of points to keep (default: 50)
 * @returns Simplified polygon coordinates
 */
export const simplifyPolygon = (
  coordinates: number[][][],
  maxPoints = 50
): number[][][] => {
  const totalPoints = coordinates.reduce((sum, ring) => sum + ring.length, 0)

  if (totalPoints <= maxPoints) return coordinates

  const simplifiedCoordinates = coordinates.map((ring) => {
    const keepFraction = maxPoints / totalPoints
    const keepPoints = Math.max(4, Math.floor(ring.length * keepFraction))
    const step = Math.floor(ring.length / keepPoints)

    const simplified = []
    for (let i = 0; i < ring.length; i += step) {
      simplified.push(ring[i])
    }

    // Ensure the ring is closed
    if (
      simplified.length > 0 &&
      (simplified[0][0] !== simplified[simplified.length - 1][0] ||
        simplified[0][1] !== simplified[simplified.length - 1][1])
    ) {
      simplified.push(simplified[0])
    }

    return simplified
  })

  return simplifiedCoordinates
}

interface MapboxStaticImageOptions {
  lat: number
  lon: number
  width?: number
  height?: number
  zoom?: number
  geoPolygon?: {
    type: 'Polygon'
    coordinates: number[][][]
  }
  polygonStyle?: {
    fill?: string
    fillOpacity?: number
    stroke?: string
    strokeWidth?: number
    strokeOpacity?: number
  }
  maxUrlLength?: number
}

/**
 * Generate a Mapbox Static API URL for a satellite image with optional polygon overlay.
 *
 * @param options - Configuration options for the static image
 * @returns Mapbox Static API URL
 */
export const generateMapboxStaticImageUrl = ({
  lat,
  lon,
  width = 300,
  height = 169,
  zoom = 17,
  geoPolygon,
  polygonStyle = {
    fill: '#00ade6',
    fillOpacity: 0.3,
    stroke: '#00ade6',
    strokeWidth: 2,
    strokeOpacity: 0.9,
  },
  maxUrlLength = 4096,
}: MapboxStaticImageOptions): string => {
  if (!MAPBOX_TOKEN) {
    console.warn('Mapbox access token is not configured')
    return ''
  }

  const center: [number, number] = [lon, lat]

  // If no polygon, just center the map on the location
  if (
    !geoPolygon ||
    !geoPolygon.coordinates ||
    geoPolygon.coordinates.length === 0
  ) {
    return `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/${center[0]},${center[1]},${zoom}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`
  }

  // Normalize and validate polygon coordinates
  const normalizedCoordinates = normalizePolygonCoordinates(
    geoPolygon.coordinates
  )

  // If normalization failed, fall back to center point
  if (normalizedCoordinates.length === 0) {
    return `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/${center[0]},${center[1]},${zoom}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`
  }

  // Simplify polygon to reduce URL length
  // Start with aggressive simplification and adjust if URL is still too long
  let maxPoints = 30
  let url = ''
  let attempts = 0

  while (attempts < 5) {
    const simplifiedCoordinates = simplifyPolygon(
      normalizedCoordinates,
      maxPoints
    )

    const geoJson = {
      type: 'Feature',
      properties: {
        fill: polygonStyle.fill,
        'fill-opacity': polygonStyle.fillOpacity,
        stroke: polygonStyle.stroke,
        'stroke-width': polygonStyle.strokeWidth,
        'stroke-opacity': polygonStyle.strokeOpacity,
      },
      geometry: {
        type: 'Polygon',
        coordinates: simplifiedCoordinates,
      },
    }

    const geoJsonString = encodeURIComponent(JSON.stringify(geoJson))
    url = `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/geojson(${geoJsonString})/${center[0]},${center[1]},${zoom}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`

    // Check URL length
    if (url.length <= maxUrlLength) {
      return url
    }

    // Reduce points and try again
    maxPoints = Math.max(8, Math.floor(maxPoints * 0.6))
    attempts++
  }

  // If still too long after all attempts, fall back to center point without polygon
  console.warn('Polygon too complex to fit in URL, using center point only')
  return `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/${center[0]},${center[1]},${zoom}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`
}
